import { capitalCase } from "change-case";
import { Bestilling, Status, Tariff } from "codegen";
import { addDays, isBefore, parseISO } from "date-fns";
import { Fragment } from "react/jsx-runtime";
import styled, { css, keyframes } from "styled-components";

import { faArrowRotateRight, faArrowUpRightFromSquare, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClearIconButton from "@ntemarked/mittne-components/dist/ClearIconButton/ClearIconButton";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import TextCopyButton from "components/molecules/TextCopyButton";
import { AccordionTable } from "components/organisms/AccordionTable";
import useAccess from "hooks/useAccess";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { OrderStatus } from "pages/Strombestilling/orders/components/OrderStatus";
import { StoppPurringerButton } from "pages/Strombestilling/orders/components/StoppPurringerButton";
import { getStrombestillingBaseLink, getUcBaseLink } from "utils/Utils";

function norwegianize(word: string) {
	return word.toLowerCase().replaceAll("aa", "å").replaceAll("oe", "ø");
}

function shouldDisplayStoppPurringButton(purrestatus: string) {
	return purrestatus === "PAAGAAR" || purrestatus === "VENTER";
}

interface BestillingWithExtraFields extends Bestilling {
	plusskunde: boolean;
	kundeNavn: string;
}

function addFlagg(b: Bestilling): BestillingWithExtraFields {
	return {
		...b,
		plusskunde: b.maalepunkter?.some((mp) => mp.erProduksjon) ?? false,
		kundeNavn: b.kunde?.fornavn + " " + b.kunde?.etternavn,
	};
}

function allowRetry(ordre: Bestilling) {
	// Feilmelding feltet kan bestå av flere ulike feilmeldinger om ordren prøves flere ganger.
	const feilmelding = ordre.feilmelding?.toLowerCase();
	return (
		ordre.status === "FEILET" &&
		(feilmelding?.includes("timeout") ||
			feilmelding?.includes("unauthorized") ||
			feilmelding?.includes("too many requests") ||
			feilmelding?.includes("internal server error")) &&
		!feilmelding?.includes("duplicate contract")
	);
}

function canBeBilledForStartCosts(startDato?: string, opprettet?: string): boolean {
	if (!startDato || !opprettet) return false;

	const start = parseISO(startDato);
	const created = parseISO(opprettet);
	const fourteenDaysAfterCreated = addDays(created, 14);

	return isBefore(start, fourteenDaysAfterCreated);
}

export function OrderTable({ bestillinger, loading, error }: Readonly<{ bestillinger: Bestilling[]; loading: boolean; error: boolean }>) {
	const rerunMutation = useStrombestillingApiMutation(`/maintenance/orders/rerun-registration`, ["orders"], "POST");
	const access = useAccess();

	const loadingMutation = rerunMutation.isPending;

	return (
		<AccordionTable
			columnPercentages={[8, 8, 13, 5, 12, 8, 15, 8]}
			loading={loading}
			error={error}
			entries={bestillinger.map((b) => addFlagg(b))}
			rowKey={(entry) => entry.id!}
			labels={{
				opprettet: "Dato",
				startDato: "Oppstart",
				status: "Status",
				purrestatus: "Purring",
				kundeNavn: "Kunde",
				plusskunde: "Plusskunde",
				product: "Produkt",
				salgskanal: "Salgskanal",
			}}
			generators={{
				opprettet: (o: Bestilling) => new Date(o.opprettet!).toLocaleDateString("no", { day: "2-digit", month: "2-digit", year: "numeric" }),
				startDato: (o: Bestilling) => new Date(o.startDato!).toLocaleDateString("no", { day: "2-digit", month: "2-digit", year: "numeric" }),
				status: (o: Bestilling) => {
					if (allowRetry(o))
						return (
							<Flex alignItems={Justify.center}>
								<OrderStatus status={o.status!} />
								{access.includes("admin-order") && (
									<RotatingIconButton
										icon={faArrowRotateRight}
										title="Kjør ordre på nytt"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											rerunMutation.mutate({
												orderId: o.id,
											});
										}}
										$rotate={loadingMutation}
									/>
								)}
							</Flex>
						);
					return <OrderStatus status={o.status!} />;
				},
				plusskunde: (o: BestillingWithExtraFields) => <>{o.plusskunde ? "Ja" : "Nei"}</>,
				kundeNavn: (o: Bestilling) =>
					o.kunde ? (
						<Flex alignItems={Justify.center} gap="0.5rem">
							{o.kunde.fornavn} {o.kunde.etternavn}
							<a
								onClick={(e) => e.stopPropagation()}
								title="Søk etter appbruker i adminportalen"
								href={`/users?search="${o.kunde.fornavn}+${o.kunde.etternavn}"`}
								target="_blank"
								rel="noreferrer"
							>
								<FontAwesomeIcon icon={faSearch} />
							</a>
							{o.kunde.kundeGuid && (
								<a
									onClick={(e) => e.stopPropagation()}
									title="Kontraktsdetaljer i UC"
									href={`${getUcBaseLink()}/#/customer/${o.kunde.kundeGuid}`}
									target="_blank"
									rel="noreferrer"
								>
									<img src="/assets/icons/uc-logo.svg" alt="Kontraktsdetaljer i UtilityCloud" style={{ height: "1rem", width: "auto" }} />
								</a>
							)}
						</Flex>
					) : (
						<i>Anonymisert</i>
					),
				purrestatus: (o: Bestilling) => (
					<Flex gap="0.5rem">
						{capitalCase(norwegianize(o.purrestatus || ""))}
						{shouldDisplayStoppPurringButton(o.purrestatus!) && (
							<StoppPurringerButton orderId={o.id!} name={o.kunde?.fornavn + " " + o.kunde?.etternavn} />
						)}
					</Flex>
				),
				product: (o: Bestilling) => capitalCase(o.product?.navn!),
				salgskanal: (o: Bestilling) => capitalCase(o.salgskanal!, { delimiter: "/" }),
			}}
			sorters={{
				product: ((a: Tariff, b: Tariff) => {
					return a.navn!.toLowerCase() > b.navn!.toLowerCase() ? 1 : -1;
				}) as (a: unknown, b: unknown) => number,
				purrestatus: ((a: string, b: string) => {
					// Må mappe om null til tom streng for at sortering skal funke
					return (a ?? "") > (b ?? "") ? 1 : -1;
				}) as (a: unknown, b: unknown) => number,
				plusskunde: ((a: boolean, b: boolean) => {
					return Number(b) - Number(a);
				}) as (a: unknown, b: unknown) => number,
			}}
			content={(o: Bestilling) => (
				<Flex direction={Direction.column} gap="0.8rem" style={{ margin: "10px 0" }}>
					<BestillingHeader>Bestilling</BestillingHeader>
					{(o.maalepunkter?.length ?? 0) > 1 && <BestillingInfo>Målepunkter</BestillingInfo>}
					{o.maalepunkter?.map((mp) => (
						<Fragment key={mp.maalepunktId}>
							<BestillingInfo>Målepunkt: {mp.maalepunktId}</BestillingInfo>
							<BestillingInfo>Målernummer: {mp.maalerId}</BestillingInfo>
						</Fragment>
					))}
					<BestillingInfo>
						Adresse: {o.kunde?.adresse?.gate} {o.kunde?.adresse?.husnummer}, {o.kunde?.adresse?.postnummer} {o.kunde?.adresse?.poststed}
					</BestillingInfo>
					<BestillingInfo>Mobil: {o.kunde?.mobil ?? "Ukjent"}</BestillingInfo>
					<BestillingInfo>Har godkjent fakturering i oppstart: {canBeBilledForStartCosts(o.startDato, o.opprettet) ? "Ja" : "Nei"}</BestillingInfo>
					<a href={`${getStrombestillingBaseLink()}/admin/ordreoversikt/${o.id}`} target="_blank" rel="noopener noreferrer">
						Gå til bestillingsdetaljer <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
					</a>
					{o.status === Status.AvventerSamtykke && o.signeringsUrl && (
						<div>
							Kopier URL til signering <TextCopyButton text={o.signeringsUrl} title={"Kopier URL til signering"} />
						</div>
					)}
				</Flex>
			)}
		/>
	);
}

const BestillingHeader = styled.p`
	font-weight: 600;
	font-size: 1.375rem;
	margin: 0;
`;
const BestillingInfo = styled.p`
	font-size: 1rem;
	line-height: 1.3;
	margin: 0;
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const RotatingIconButton = styled(ClearIconButton)<{ $rotate?: boolean }>`
	svg {
		${(p) =>
			p.$rotate &&
			css`
				animation: ${rotate} 1s linear infinite;
			`}
	}
`;
